import * as React from 'react';
import { FC, useMemo, useState, useCallback } from 'react';
import { FormField } from '../../fields';
import { ReportFieldFlag, ReportFieldType } from '../../../../models';
import { IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import EditIcon from '@material-ui/icons/Edit';
import CodeIcon from '@material-ui/icons/Code';
import { ActionsContainer, AiSuggestionChip, AiSuggestionLabel, AiSuggestionText } from './styled';
import Typography from '../../../Typography';
import { ReportFieldDefinition } from '../../../../models';
import HelpIcon from '@material-ui/icons/Help';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import ImageMarkerField from './ImageMarkerReportField';
import { ReportTableField } from './ReportTableReportField';
import { SingleLineTextField } from './SingleLineTextReportField';
import { MultiLineTextField } from './MultiLineTextReportField';
import { IntegerField } from './IntegerReportField';
import { FloatField } from './FloatReportField';
import { DateReportField } from './DateReportField';
import { SeparatorField } from './SeparatorReportField';
import { ScaleField } from './ScaleReportField';
import { MultiOptionsField } from './MultiOptionsReportField';
import { ReportSignatureField } from './ReportSignatureField';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import AppButton from '../../../AppButton';
import { PlaceholderMenu, insertPlaceholderAtCursor } from '../../fields/PlaceholderTextField';

export type ReportFieldEditAction = 'delete'|'edit'|'move-up'|'move-down';

interface ReportFieldProps {
    id: string,
    fieldDefinition: ReportFieldDefinition,
    value?: string,
    showEditActions?: boolean,
    readOnly?: boolean,
    error?: string,
    hideTitle?: boolean,
    aiSuggestion?: string,
    onChange?: (value: string) => void,
    onAction?: (action: ReportFieldEditAction) => void,
    onAcceptAiSuggestion?: (value: string) => void,
}

const ReportFieldHeader: FC<ReportFieldProps & {showOptions?: boolean, onShowHelp?: () => void, onInsertPlaceholder?: (e: React.MouseEvent<HTMLElement>) => void}> = ({
    id,
    fieldDefinition: { label, description, helpNotes, isMandatory },
    readOnly,
    showOptions,
    showEditActions,
    hideTitle,
    onAction,
    onShowHelp,
    onInsertPlaceholder,
}) => (
    <>
        {!hideTitle &&
            <Typography id={`${id}-header`} block style={{margin:0}}>
                <b>{label}</b>{isMandatory && !readOnly ? '*' : ''}
                {helpNotes && !readOnly && <IconButton size="small" onClick={onShowHelp}><HelpIcon/></IconButton>}
            </Typography>
        }
        {description && !readOnly && <Typography id={`${id}-description`} size="small">{description}</Typography>}
        {!readOnly && showOptions && showEditActions && onAction &&
            <ActionsContainer>
                {onInsertPlaceholder && <IconButton id={`${id}-placeholder-button`} size="small" onClick={onInsertPlaceholder}><CodeIcon/></IconButton>}
                <IconButton id={`${id}-edit-button`} size="small" onClick={() => onAction('edit')}><EditIcon/></IconButton>
                <IconButton id={`${id}-move-up-button`} size="small" onClick={() => onAction('move-up')}><ArrowUpwardIcon/></IconButton>
                <IconButton id={`${id}-mode-down-button`} size="small" onClick={() => onAction('move-down')}><ArrowDownwardIcon/></IconButton>
                <IconButton id={`${id}-delete-button`} size="small" onClick={() => onAction('delete')}><ClearIcon/></IconButton>
            </ActionsContainer>
        }
    </>
);

const ReportField: FC<ReportFieldProps> = React.memo((props) => {

    const t = useTranslations();

    const {
        fieldDefinition,
        error,
        readOnly,
        value,
        onChange,
        showEditActions,
        aiSuggestion,
        onAcceptAiSuggestion,
    } = props;

    const handleAccept = useCallback(() => {
        if (aiSuggestion !== undefined) {
            onAcceptAiSuggestion?.(aiSuggestion);
        }
    }, [aiSuggestion, onAcceptAiSuggestion]);

    const showAiSuggestion = !readOnly && aiSuggestion !== undefined && value !== aiSuggestion;

    const { label, type, helpNotes, flags, fontSize } = fieldDefinition;

    const [showOptions, setShowOptions] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [placeholderAnchorEl, setPlaceholderAnchorEl] = useState<HTMLElement | null>(null);

    const isTextField = type === ReportFieldType.Text || type === ReportFieldType.MultilineText;
    const onInsertPlaceholder = showEditActions && isTextField
        ? () => setPlaceholderAnchorEl(document.getElementById(`${props.id}-placeholder-button`))
        : undefined;

    const closePlaceholderMenu = useCallback(() => {
        setPlaceholderAnchorEl(null);
        setShowOptions(false);
    }, []);

    const style: React.CSSProperties = {
        fontSize: fontSize ? `${fontSize}px` : undefined,
        textAlign: flags?.includes(ReportFieldFlag.Centered) ? 'center' : undefined,
        justifyItems: flags?.includes(ReportFieldFlag.AlignRight) ? 'right' : undefined,
        width: flags?.includes(ReportFieldFlag.Block) ? '100%' : undefined,
    }

    const fieldProps = {
        ...props,
        style: {
            ...style,
            height: fieldDefinition.height ? `${fieldDefinition.height}rem` : undefined,
        },
        readOnly: readOnly || flags?.includes(ReportFieldFlag.ReadOnly),
        invalid: !!error,
    }

    const hideHeader = useMemo(() => type === ReportFieldType.Separator || flags?.includes(ReportFieldFlag.HideLabel), [flags, type]);

    return (
        <>
            <FormField
                label={<ReportFieldHeader {...props} hideTitle={hideHeader} showOptions={showOptions} onShowHelp={() => setShowHelp(true)} onInsertPlaceholder={onInsertPlaceholder}/>}
                error={error}
                style={style}
                formGroupStyle={style}
                onMouseEnter={() => setShowOptions(true)}
                onMouseLeave={() => { if (!placeholderAnchorEl) setShowOptions(false); }}
            >
                {
                    type === ReportFieldType.Text ? <SingleLineTextField {...fieldProps}/>
                    : type === ReportFieldType.MultilineText ? <MultiLineTextField {...fieldProps}/>
                    : type === ReportFieldType.IntNumber ? <IntegerField {...fieldProps}/>
                    : type === ReportFieldType.FloatNumber ? <FloatField {...fieldProps}/>
                    : type === ReportFieldType.Date ? <DateReportField {...fieldProps}/>
                    : type === ReportFieldType.ImageMarker ? <ImageMarkerField {...fieldProps}/>
                    : type === ReportFieldType.Separator ? <SeparatorField {...fieldProps}/>
                    : type === ReportFieldType.Scale ? <ScaleField {...fieldProps}/>
                    : type === ReportFieldType.MultiOptions ? <MultiOptionsField {...fieldProps}/>
                    : type === ReportFieldType.Table ? <ReportTableField {...fieldProps}/>
                    : type === ReportFieldType.Signature ? <ReportSignatureField {...fieldProps}/>
                    : type === ReportFieldType.BlankSpace ? <div style={{ background: readOnly ? undefined : 'whitesmoke', ...fieldProps.style, height: fieldProps.style?.height ?? '3rem' }}/>
                    : <>ERROR</>
                }
            </FormField>
            {(helpNotes?.length &&
                <Modal isOpen={showHelp} toggle={() => setShowHelp(false)}>
                    <ModalHeader>{label}</ModalHeader>
                    <ModalBody>
                        {helpNotes
                            .split('\n')
                            .map(line => line.trimStart().startsWith('-')
                                ? <li key={line}>{line.substr(line.indexOf('-')+1)}</li>
                                : <Typography key={line} block>{line}</Typography>)}
                    </ModalBody>
                </Modal>
            ) || undefined}
            {isTextField && showEditActions && (
                <PlaceholderMenu
                    anchorEl={placeholderAnchorEl}
                    onClose={closePlaceholderMenu}
                    onInsert={(key) => {
                        closePlaceholderMenu();
                        insertPlaceholderAtCursor(key, `${props.id}-input`, String(value ?? ''), onChange);
                    }}
                />
            )}
            {showAiSuggestion && (
                <AiSuggestionChip>
                    <AiSuggestionLabel>{t(TK.aiScriberAiLabel)}:</AiSuggestionLabel>
                    <AiSuggestionText>{aiSuggestion}</AiSuggestionText>
                    <AppButton color="link" size="sm" style={{ padding: 0, flexShrink: 0 }} onClick={handleAccept}>
                        {t(TK.aiScriberAccept)}
                    </AppButton>
                </AiSuggestionChip>
            )}
        </>
    );
});

export default ReportField;
